import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { HasErrors } from "../../common/Logic/ServerResponse";
import { initialState } from "./thunks/adminInitialState";
import { AdminInitialState } from "./thunks/adminTypes";
import { UpdateApplicationStatusDto } from "./models";
import * as thunks from "./thunks";

// Utility functions for handling common reducer patterns
const handlePending = (state: AdminInitialState, key: keyof AdminInitialState) => {
  state[key].status = "loading";
};

const handleRejected = (
  state: AdminInitialState,
  key: keyof AdminInitialState,
  action: any
) => {
  state[key].status = "failed";
  state[key].error = action.payload || action.error;
};

const handleFulfilled = (
  state: AdminInitialState,
  key: keyof AdminInitialState,
  subKey: string,
  action: any
) => {
  const hasErrors = HasErrors(action);
  if (hasErrors === 1) {
    state[key].status = "failed";
    state[key].error = action.payload;
  } else if (hasErrors === 3 || hasErrors === 2) {
    if (subKey) {
      (state[key] as any)[subKey] = action.payload.data;
    }
    state[key].status = "idle";
  }
};

const handleFulfilledArrayPush = (
  state: AdminInitialState,
  key: keyof AdminInitialState,
  subKey: string,
  action: any
) => {
  const hasErrors = HasErrors(action);
  if (hasErrors === 1) {
    state[key].status = "failed";
    state[key].error = action.payload;
  } else if (hasErrors === 2 || hasErrors === 3) {
    state[key].status = "idle";
    (state[key] as any)[subKey].push(action.payload.data);
  }
};

const handleFulfilledUpdateItem = (
  state: AdminInitialState,
  key: keyof AdminInitialState,
  subKey: string,
  action: any,
  idKey: string = 'id'
) => {
  const hasErrors = HasErrors(action);
  if (hasErrors === 1) {
    state[key].status = "failed";
    state[key].error = action.payload;
  } else if (hasErrors === 3 || hasErrors === 2) {
    const responseData = action.payload.data;
    const dataList = (state[key] as any)[subKey];
    const itemToUpdate = dataList.find((item: any) => item[idKey] === responseData[idKey]);

    if (itemToUpdate) {
      Object.assign(itemToUpdate, responseData);
    }
    state[key].status = "idle";
  }
};

const handleFulfilledFilter = (
  state: AdminInitialState,
  key: keyof AdminInitialState,
  subKey: string,
  action: any,
  idKey: string = 'id'
) => {
  const hasErrors = HasErrors(action);
  if (hasErrors === 1) {
    state[key].status = "failed";
    state[key].error = action.payload;
  } else if (hasErrors === 3 || hasErrors === 2) {
    (state[key] as any)[subKey] = (state[key] as any)[subKey].filter(
      (item: any) => item[idKey] !== action.payload.data
    );
    state[key].status = "idle";
  }
};

export const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    updateApplicants(state, action: PayloadAction<UpdateApplicationStatusDto>) {
      const { applicationId, applicationStatus } = action.payload;
      const index = state.applicantState.applicants.findIndex(
        (s) => s.applicationId === applicationId
      );
      if (index !== -1) {
        state.applicantState.applicants[index].applicationStage = applicationStatus;
      }
    },
  },
  extraReducers: (builder) => {
    // Dashboard thunks
    builder
      .addCase(thunks.markNotificationAsReadThunk.pending, (state) => {
        handlePending(state, 'dashboardState');
      })
      .addCase(thunks.markNotificationAsReadThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.dashboardState.status = "failed";
          state.dashboardState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.dashboardState.notifications = state.dashboardState.notifications.filter(
            notification => notification.id !== action.payload.data
          );
          state.dashboardState.markAsRead = action.payload.data;
          state.dashboardState.status = "idle";
        }
      })
      .addCase(thunks.markNotificationAsReadThunk.rejected, (state, action) => {
        handleRejected(state, 'dashboardState', action);
      });

    builder
      .addCase(thunks.fetchNotificationsThunk.pending, (state) => {
        handlePending(state, 'dashboardState');
      })
      .addCase(thunks.fetchNotificationsThunk.fulfilled, (state, action) => {
        handleFulfilled(state, 'dashboardState', 'notifications', action);
      })
      .addCase(thunks.fetchNotificationsThunk.rejected, (state, action) => {
        handleRejected(state, 'dashboardState', action);
      });

    builder
      .addCase(thunks.fetchDashboardThunk.pending, (state, action) => {
        handlePending(state, 'dashboardState');
        state.dashboardState.latestDashboardYearRequest = action.meta.arg as number;
      })
      .addCase(thunks.fetchDashboardThunk.fulfilled, (state, action) => {
        const year = action.meta.arg as number;
        if (state.dashboardState.latestDashboardYearRequest !== year) {
          return;
        }
        handleFulfilled(state, 'dashboardState', 'dashboard', action);
      })
      .addCase(thunks.fetchDashboardThunk.rejected, (state, action) => {
        const year = action.meta.arg as number;
        if (state.dashboardState.latestDashboardYearRequest !== year) {
          return;
        }
        handleRejected(state, 'dashboardState', action);
      });

    // Document thunks
    builder
      .addCase(thunks.CreateDocumentAPIThunk.pending, (state) => {
        handlePending(state, 'applicantDocumentState');
      })
      .addCase(thunks.CreateDocumentAPIThunk.fulfilled, (state, action) => {
        handleFulfilled(state, 'applicantDocumentState', 'createDocument', action);
      })
      .addCase(thunks.CreateDocumentAPIThunk.rejected, (state, action) => {
        handleRejected(state, 'applicantDocumentState', action);
      });

    builder
      .addCase(thunks.GetDocumentsForApplicantThunk.pending, (state) => {
        handlePending(state, 'applicantDocumentState');
      })
      .addCase(thunks.GetDocumentsForApplicantThunk.fulfilled, (state, action) => {
        handleFulfilled(state, 'applicantDocumentState', 'applicantDocuments', action);
      })
      .addCase(thunks.GetDocumentsForApplicantThunk.rejected, (state, action) => {
        handleRejected(state, 'applicantDocumentState', action);
      });

    // Applicant thunks
    builder
      .addCase(thunks.fetchApplicationYearsThunk.pending, (state) => {
        handlePending(state, 'applicantState');
      })
      .addCase(thunks.fetchApplicationYearsThunk.fulfilled, (state, action) => {
        handleFulfilled(state, 'applicantState', 'applicationYears', action);
      })
      .addCase(thunks.fetchApplicationYearsThunk.rejected, (state, action) => {
        handleRejected(state, 'applicantState', action);
      });

    builder
      .addCase(thunks.fetchAllApplicantThunk.pending, (state) => {
        handlePending(state, 'applicantState');
      })
      .addCase(thunks.fetchAllApplicantThunk.fulfilled, (state, action) => {
        handleFulfilled(state, 'applicantState', 'applicants', action);
      })
      .addCase(thunks.fetchAllApplicantThunk.rejected, (state, action) => {
        handleRejected(state, 'applicantState', action);
      });

    builder
      .addCase(thunks.ExportToExcelThunk.pending, (state) => {
        state.exportState = { 
          ...state.exportState,
          status: 'loading',
          isExporting: true,
          error: null 
        };
      })
      .addCase(thunks.ExportToExcelThunk.fulfilled, (state, action) => {
        state.exportState = { 
          ...state.exportState,
          status: 'idle',
          isExporting: false,
          error: null 
        };
      })
      .addCase(thunks.ExportToExcelThunk.rejected, (state, action) => {
        state.exportState = { 
          ...state.exportState,
          status: 'failed',
          isExporting: false,
          error: action.payload || action.error || 'Export failed' 
        };
      });

    builder
      .addCase(thunks.UpdatePersonalDetailsThunk.pending, (state) => {
        handlePending(state, 'applicationDatesState');
      })
      .addCase(thunks.UpdatePersonalDetailsThunk.fulfilled, (state, action) => {
        handleFulfilled(state, 'applicantState', 'applicants', action);
        state.applicationDatesState.status = "idle";
      })
      .addCase(thunks.UpdatePersonalDetailsThunk.rejected, (state, action) => {
        handleRejected(state, 'applicationDatesState', action);
      });

    // Application Dates thunks
    builder
      .addCase(thunks.setApplicationDatesThunk.pending, (state) => {
        handlePending(state, 'applicationDatesState');
      })
      .addCase(thunks.setApplicationDatesThunk.fulfilled, (state, action) => {
        handleFulfilledArrayPush(state, 'applicationDatesState', 'getApplicationDates', action);
      })
      .addCase(thunks.setApplicationDatesThunk.rejected, (state, action) => {
        handleRejected(state, 'applicationDatesState', action);
      });

    builder
      .addCase(thunks.getApplicationDatesThunk.pending, (state) => {
        handlePending(state, 'applicationDatesState');
      })
      .addCase(thunks.getApplicationDatesThunk.fulfilled, (state, action) => {
        handleFulfilled(state, 'applicationDatesState', 'getApplicationDates', action);
      })
      .addCase(thunks.getApplicationDatesThunk.rejected, (state, action) => {
        handleRejected(state, 'applicationDatesState', action);
      });

    builder
      .addCase(thunks.UpdateApplicationStatusThunk.pending, (state) => {
        handlePending(state, 'applicationDatesState');
      })
      .addCase(thunks.UpdateApplicationStatusThunk.fulfilled, (state, action) => {
        handleFulfilled(state, 'applicantState', 'updateStatus', action);
        state.applicationDatesState.status = "idle";
      })
      .addCase(thunks.UpdateApplicationStatusThunk.rejected, (state, action) => {
        handleRejected(state, 'applicationDatesState', action);
      });

    // Assessment thunks
    builder
      .addCase(thunks.getAssessmentAuditsThunk.pending, (state) => {
        handlePending(state, 'assessmentAuditsState');
      })
      .addCase(thunks.getAssessmentAuditsThunk.fulfilled, (state, action) => {
        handleFulfilled(state, 'assessmentAuditsState', 'assessmentAudits', action);
      })
      .addCase(thunks.getAssessmentAuditsThunk.rejected, (state, action) => {
        handleRejected(state, 'assessmentAuditsState', action);
      });

    builder
      .addCase(thunks.setApplicationAssessmentThunk.pending, (state) => {
        handlePending(state, 'applicationDatesState');
      })
      .addCase(thunks.setApplicationAssessmentThunk.fulfilled, (state, action) => {
        handleFulfilled(state, 'assessmentAuditsState', 'setAssessment', action);
        state.applicationDatesState.status = "idle";
      })
      .addCase(thunks.setApplicationAssessmentThunk.rejected, (state, action) => {
        handleRejected(state, 'applicationDatesState', action);
      });

    // Decline Reason thunks
    builder
      .addCase(thunks.addDeclineReasonThunk.pending, (state) => {
        handlePending(state, 'adminApplicantDeclineState');
      })
      .addCase(thunks.addDeclineReasonThunk.fulfilled, (state, action) => {
        handleFulfilledArrayPush(state, 'adminApplicantDeclineState', 'declineReasons', action);
      })
      .addCase(thunks.addDeclineReasonThunk.rejected, (state, action) => {
        handleRejected(state, 'adminApplicantDeclineState', action);
      });

    builder
      .addCase(thunks.AllDeclineReasonsThunk.pending, (state) => {
        handlePending(state, 'adminApplicantDeclineState');
      })
      .addCase(thunks.AllDeclineReasonsThunk.fulfilled, (state, action) => {
        handleFulfilled(state, 'adminApplicantDeclineState', 'declineReasons', action);
      })
      .addCase(thunks.AllDeclineReasonsThunk.rejected, (state, action) => {
        handleRejected(state, 'adminApplicantDeclineState', action);
      });

    builder
      .addCase(thunks.EditDeclineReasonThunk.pending, (state) => {
        handlePending(state, 'adminApplicantDeclineState');
      })
      .addCase(thunks.EditDeclineReasonThunk.fulfilled, (state, action) => {
        handleFulfilledUpdateItem(state, 'adminApplicantDeclineState', 'declineReasons', action);
      })
      .addCase(thunks.EditDeclineReasonThunk.rejected, (state, action) => {
        handleRejected(state, 'adminApplicantDeclineState', action);
      });

    builder
      .addCase(thunks.DeleteDeclineReasonThunk.pending, (state) => {
        handlePending(state, 'adminApplicantDeclineState');
      })
      .addCase(thunks.DeleteDeclineReasonThunk.fulfilled, (state, action) => {
        handleFulfilledFilter(state, 'adminApplicantDeclineState', 'declineReasons', action);
      })
      .addCase(thunks.DeleteDeclineReasonThunk.rejected, (state, action) => {
        handleRejected(state, 'adminApplicantDeclineState', action);
      });
  },
});

export const { updateApplicants } = adminSlice.actions;
export const selectAdmin = (state: RootState) => state.admin;
export default adminSlice.reducer;